import {Component, EventEmitter, HostListener, Input, Output} from '@angular/core';
import {PHONE_COUNTRIES, PhoneCountry} from './phone-countries';

@Component({
    selector: 'ui-phone-field',
    templateUrl: './phone-field.component.html',
    styles: [':host { display: block; min-width: 0; }']
})
export class PhoneFieldComponent {
    @Input() inputId = 'phone-field';
    @Input() name = 'phone';
    @Input() label = 'Telefono';
    @Input() placeholder = '333 123 4567';
    @Input() required = false;
    @Input() showHint = true;
    @Input() value = '';

    @Output() valueChange = new EventEmitter<string>();

    readonly phoneCountries = PHONE_COUNTRIES;
    isPhoneCountryOpen = false;

    private selectedPhoneDialCode = '+39';

    get selectedPhoneCountry(): PhoneCountry {
        return this.phoneCountries.find((country) => country.dialCode === this.phoneCountryDialCode)
            ?? this.phoneCountries[0];
    }

    get phoneCountryDialCode(): string {
        return this.detectPhoneCountry()?.dialCode ?? this.selectedPhoneDialCode;
    }

    set phoneCountryDialCode(dialCode: string) {
        const nationalNumber = this.phoneNationalNumber;
        this.selectedPhoneDialCode = dialCode;
        this.emitValue(nationalNumber ? `${dialCode}${nationalNumber}` : '');
    }

    get phoneNationalNumber(): string {
        const rawPhone = this.value?.trim() ?? '';
        const country = this.detectPhoneCountry();
        const withoutPrefix = country && rawPhone.startsWith(country.dialCode)
            ? rawPhone.slice(country.dialCode.length)
            : rawPhone.replace(/^\+/, '');
        return withoutPrefix.replace(/\D/g, '');
    }

    get formattedPhoneNationalNumber(): string {
        return this.formatNationalNumber(this.phoneNationalNumber);
    }

    get isPhoneValid(): boolean {
        if (!(this.value ?? '').trim()) {
            return !this.required;
        }
        const digitCount = (this.value ?? '').replace(/\D/g, '').length;
        return digitCount >= 7 && digitCount <= 15;
    }

    togglePhoneCountryDropdown(): void {
        this.isPhoneCountryOpen = !this.isPhoneCountryOpen;
    }

    selectPhoneCountry(country: PhoneCountry): void {
        this.phoneCountryDialCode = country.dialCode;
        this.isPhoneCountryOpen = false;
    }

    onPhoneInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const digitsBeforeCursor = input.value
            .slice(0, input.selectionStart ?? input.value.length)
            .replace(/\D/g, '').length;
        const digits = (input.value ?? '').replace(/\D/g, '').slice(0, 14);
        this.emitValue(digits ? `${this.phoneCountryDialCode}${digits}` : '');
        const formatted = this.formattedPhoneNationalNumber;
        input.value = formatted;
        const position = this.cursorFromDigitIndex(formatted, digitsBeforeCursor);
        input.setSelectionRange(position, position);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('[data-phone-country]')) {
            this.isPhoneCountryOpen = false;
        }
    }

    private emitValue(value: string): void {
        this.value = value;
        this.valueChange.emit(value);
    }

    private detectPhoneCountry(): PhoneCountry | undefined {
        const phone = this.value?.trim() ?? '';
        return [...this.phoneCountries]
            .sort((a, b) => b.dialCode.length - a.dialCode.length)
            .find((country) => phone.startsWith(country.dialCode));
    }

    private formatNationalNumber(digits: string): string {
        if (!digits) {
            return '';
        }

        if (this.selectedPhoneCountry.iso === 'IT') {
            if (digits.startsWith('3')) {
                return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)]
                    .filter(Boolean)
                    .join(' ');
            }
            const areaLength = /^0(2|6)/.test(digits) ? 2 : Math.min(3, digits.length);
            const rest = digits.slice(areaLength).replace(/(\d{3})(?=\d)/g, '$1 ').trim();
            return [digits.slice(0, areaLength), rest].filter(Boolean).join(' ');
        }

        return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    }

    private cursorFromDigitIndex(formatted: string, digitIndex: number): number {
        if (digitIndex <= 0) {
            return 0;
        }
        let seen = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) {
                seen++;
                if (seen === digitIndex) {
                    return i + 1;
                }
            }
        }
        return formatted.length;
    }
}
